import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Stack,
} from '@mui/material';
import { format } from 'date-fns';
import { checkComponentPermission } from '../../../utils/share';
import Iconify from '../../../components/iconify';
import TablePaginationCustom from '../../../components/table/table-pagination-custom';
import ReimbursementStatusBadge from './ReimbursementStatusBadge';
import { REIMBURSEMENT_STATUS } from '../constants';

const formatDate = (dateString) => {
  if (!dateString) return '-';
  try {
    return format(new Date(dateString), 'dd-MMM-yyyy');
  } catch {
    return dateString;
  }
};

const truncate = (text, maxLength = 60) => {
  if (!text) return '-';
  return text.length > maxLength ? `${text.substring(0, maxLength)}…` : text;
};

const ReimbursementTable = ({
  reimbursements,
  onCancelReimbursement,
  onEditReimbursement,
  showEmployee = false,
  currentUserId,
  count = 0,
  page = 0,
  rowsPerPage = 10,
  onPageChange,
  onRowsPerPageChange,
}) => {
  const navigate = useNavigate();

  return (
    <>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              {showEmployee && <TableCell>Employee</TableCell>}
              <TableCell>Date</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Amount</TableCell>
              <TableCell>Attachment</TableCell>
              <TableCell>Status</TableCell>
              {showEmployee && <TableCell>Reviewed By</TableCell>}
              {showEmployee && <TableCell>Reviewed Date</TableCell>}
              <TableCell sx={{ pl: 2 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {reimbursements && reimbursements.length > 0 ? (
              reimbursements.map((reimbursement) => (
                <TableRow key={reimbursement.id} hover>
                  {showEmployee && (
                    <TableCell>
                      {reimbursement.employee?.userName || reimbursement.employee?.email || '-'}
                    </TableCell>
                  )}
                  <TableCell>{formatDate(reimbursement.date)}</TableCell>
                  <TableCell>
                    <Tooltip title={reimbursement.description || ''} placement="top">
                      <span>{truncate(reimbursement.description)}</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell>{parseFloat(reimbursement.amount || 0).toFixed(2)}</TableCell>
                  <TableCell>
                    {reimbursement.attachmentKey ? (
                      <Tooltip title="Has PDF attachment">
                        <Iconify icon="material-symbols:attach-file" sx={{ color: 'text.secondary' }} />
                      </Tooltip>
                    ) : (
                      '-'
                    )}
                  </TableCell>
                  <TableCell>
                    <ReimbursementStatusBadge status={reimbursement.status} />
                  </TableCell>
                  {showEmployee && (
                    <TableCell>
                      {reimbursement.approver && reimbursement.approver.id === currentUserId
                        ? 'Me'
                        : reimbursement.approver?.userName || reimbursement.approver?.email || '-'}
                    </TableCell>
                  )}
                  {showEmployee && <TableCell>{formatDate(reimbursement.reviewedAt)}</TableCell>}
                  <TableCell align="left">
                    <Stack direction="row" spacing={1} justifyContent="left">
                      <Tooltip title="View Details">
                        <IconButton
                          size="small"
                          color="info"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/reimbursements/${reimbursement.id}`);
                          }}
                        >
                          <Iconify icon="eva:eye-fill" />
                        </IconButton>
                      </Tooltip>
                      {!showEmployee && reimbursement.userId === currentUserId && (
                        <>
                          {reimbursement.status === REIMBURSEMENT_STATUS.PENDING &&
                            checkComponentPermission('UPDATE_REIMBURSEMENT') && (
                              <Tooltip title="Edit Request">
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onEditReimbursement(reimbursement.id);
                                  }}
                                >
                                  <Iconify icon="eva:edit-fill" />
                                </IconButton>
                              </Tooltip>
                            )}
                          {[REIMBURSEMENT_STATUS.PENDING, REIMBURSEMENT_STATUS.APPROVED].includes(
                            reimbursement.status
                          ) &&
                            checkComponentPermission('DELETE_REIMBURSEMENT') && (
                              <Tooltip title="Cancel Request">
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onCancelReimbursement(reimbursement.id);
                                  }}
                                >
                                  <Iconify icon="material-symbols:delete" />
                                </IconButton>
                              </Tooltip>
                            )}
                        </>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={showEmployee ? 9 : 6} align="center">
                  No reimbursement requests found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {count > 0 && (
        <TablePaginationCustom
          count={count}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={onPageChange}
          onRowsPerPageChange={onRowsPerPageChange}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      )}
    </>
  );
};

ReimbursementTable.propTypes = {
  reimbursements: PropTypes.array.isRequired,
  onCancelReimbursement: PropTypes.func,
  onEditReimbursement: PropTypes.func,
  showEmployee: PropTypes.bool,
  currentUserId: PropTypes.number,
  count: PropTypes.number,
  page: PropTypes.number,
  rowsPerPage: PropTypes.number,
  onPageChange: PropTypes.func,
  onRowsPerPageChange: PropTypes.func,
};

export default ReimbursementTable;
